
import { Container } from "@/components/ui/container";
import { Shield, Lock, Users, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export const GuideSection = () => {
  return (
    <section className="py-16">
      <Container>
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-white to-purple-50/30">
          <div className="absolute inset-0 bg-white/40 backdrop-blur-3xl" />
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-purple-100/30 to-transparent" />
          
          <div className="relative py-20 px-8 md:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-10">
                <div className="space-y-6">
                  <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                    Feeling Overwhelmed by Microsoft 365 Security?
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    Many organizations struggle with complex security settings, confusing compliance requirements, and the fear of making costly mistakes.
                  </p>
                </div>

                <div className="space-y-8 relative">
                  <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-edgefi-purple to-edgefi-amethyst rounded-full" aria-hidden="true" />
                  <div className="pl-8">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                      Your Path to Security Success
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      We've created a comprehensive Security Field Guide that breaks down complex security concepts into actionable steps, helping you:
                    </p>
                    <ul className="mt-6 space-y-5">
                      <li className="flex items-start gap-4">
                        <div className="p-2 bg-purple-50 rounded-lg">
                          <Shield className="h-5 w-5 text-edgefi-purple" aria-hidden="true" />
                        </div>
                        <span className="text-gray-600 leading-relaxed mt-1">Navigate security challenges with confidence</span>
                      </li>
                      <li className="flex items-start gap-4">
                        <div className="p-2 bg-purple-50 rounded-lg">
                          <Lock className="h-5 w-5 text-edgefi-purple" aria-hidden="true" />
                        </div>
                        <span className="text-gray-600 leading-relaxed mt-1">Implement proven security measures step-by-step</span>
                      </li>
                      <li className="flex items-start gap-4">
                        <div className="p-2 bg-purple-50 rounded-lg">
                          <Users className="h-5 w-5 text-edgefi-purple" aria-hidden="true" />
                        </div>
                        <span className="text-gray-600 leading-relaxed mt-1">Build a security-aware organization</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <Link 
                  to="/resources/security-guide"
                  className="group inline-flex items-center gap-3 px-6 py-3 bg-edgefi-purple/10 hover:bg-edgefi-purple/20 text-edgefi-purple font-semibold rounded-full transition-colors duration-300"
                >
                  Explore the Security Field Guide
                  <ArrowRight 
                    className="h-5 w-5 transition-transform duration-300 transform group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </Link>
              </div>

              <div className="relative">
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl p-10 border border-purple-100/50 shadow-[0_4px_20px_-4px_rgba(107,70,193,0.15)] transition-all duration-500 hover:shadow-[0_8px_30px_-4px_rgba(107,70,193,0.25)]">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-100/30 to-blue-100/30 rounded-full blur-3xl transform -translate-x-20 -translate-y-20" aria-hidden="true" />
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-blue-100/30 to-purple-100/30 rounded-full blur-3xl transform translate-x-10 translate-y-10" aria-hidden="true" />
                  
                  <div className="relative">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-8">
                      What You'll Learn
                    </h3>
                    <div className="space-y-5">
                      <div className="p-6 bg-gradient-to-br from-purple-50/70 to-white rounded-xl border border-purple-100/20">
                        <h4 className="font-medium text-gray-900">Foundation Security</h4>
                        <p className="text-gray-600 mt-2">Essential security controls and configurations</p>
                      </div>
                      <div className="p-6 bg-gradient-to-br from-blue-50/70 to-white rounded-xl border border-blue-100/20">
                        <h4 className="font-medium text-gray-900">Identity & Access</h4>
                        <p className="text-gray-600 mt-2">User authentication and permissions</p>
                      </div>
                      <div className="p-6 bg-gradient-to-br from-green-50/70 to-white rounded-xl border border-green-100/20">
                        <h4 className="font-medium text-gray-900">Data Protection</h4>
                        <p className="text-gray-600 mt-2">Information security and compliance</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};
